import React from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { useAuth0 } from "@auth0/auth0-react";

import { Circle, Clock3, MessageCircle } from "lucide-react";

import "./OssDashboard.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/* ==========================================================
   FORMATTERS
   ========================================================== */

function formatDate(value) {
  if (!value) return "—";

  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return "—";
  }
}

function formatProjectStatus(status, t) {
  const map = {
    PLANNING: t("projectStatuses.PLANNING"),

    IN_PROGRESS: t("projectStatuses.IN_PROGRESS"),

    COMPLETED: t("projectStatuses.COMPLETED"),

    CANCELLED: t("projectStatuses.CANCELLED"),

    CLOSED: t("projectStatuses.CLOSED"),
  };

  return map[status] || status || "";
}

function formatProjectStep(step, t) {
  const map = {
    PROPERTY_INFORMATION: t("projectSteps.PROPERTY_INFORMATION"),

    PRE_ASSESSMENT: t("projectSteps.PRE_ASSESSMENT"),

    RENOVATION_PLAN: t("projectSteps.RENOVATION_PLAN"),

    ENERGY_PERFORMANCE_CERTIFICATE: t(
      "projectSteps.ENERGY_PERFORMANCE_CERTIFICATE",
    ),

    // Internal backend code remains WORKS_BUDGET.
    // UI label is Home Renovation.
    WORKS_BUDGET: t("projectSteps.WORKS_BUDGET"),

    SUBSIDIES_GRANTS: t("projectSteps.SUBSIDIES_GRANTS"),

    FINANCING: t("projectSteps.FINANCING"),

    MONITORING: t("projectSteps.MONITORING"),
  };

  return map[step] || step || "";
}

/* ==========================================================
   MESSAGE HELPERS
   ========================================================== */

function getMessageContext(message) {
  return (
    message.loan_case_reference ||
    message.project_code ||
    message.dossier_code ||
    message.thread_title ||
    ""
  );
}

function truncateMessage(value, maxLength = 115) {
  if (!value) {
    return "";
  }

  const clean = String(value).replace(/\s+/g, " ").trim();

  if (clean.length <= maxLength) {
    return clean;
  }

  return `${clean.slice(0, maxLength).trim()}…`;
}

/* ==========================================================
   DASHBOARD
   ========================================================== */

export default function OssDashboard() {
  const navigate = useNavigate();

  const { t } = useTranslation("oss-dashboard");

  const { getAccessTokenSilently } = useAuth0();

  const [ossMe, setOssMe] = React.useState(null);

  const [loading, setLoading] = React.useState(true);

  const [error, setError] = React.useState("");

  const location = useLocation();

  /* ========================================================
     LOAD OSS DASHBOARD
     ======================================================== */

  React.useEffect(() => {
    let cancelled = false;

    async function loadOssMe() {
      setLoading(true);
      setError("");

      try {
        const token = await getAccessTokenSilently();

        const response = await fetch(`${API_BASE}/api/oss/me/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data?.detail ||
              t("messages.loadFailed", "Failed to load dashboard."),
          );
        }

        if (!cancelled) {
          setOssMe(data);
        }
      } catch (err) {
        console.error("Failed to load OSS dashboard context:", err);

        if (!cancelled) {
          setError(
            err?.message ||
              t("messages.loadFailed", "Failed to load dashboard."),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadOssMe();

    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, t, location.key]);

  /* ========================================================
     RESPONSE DATA
     ======================================================== */

  const dashboard = ossMe?.dashboard_summary || {};

  const latestProjectUpdates = Array.isArray(ossMe?.latest_project_updates)
    ? ossMe.latest_project_updates
    : [];

  const latestMessages = React.useMemo(() => {
    const messages = Array.isArray(ossMe?.latest_messages)
      ? ossMe.latest_messages
      : [];

    return messages.filter((message) => !message.is_deleted);
  }, [ossMe?.latest_messages]);

  const latestProjectUpdatesTotal =
    ossMe?.latest_project_updates_total ?? latestProjectUpdates.length;

  const unreadMessagesCount =
    ossMe?.unread_messages_count ?? dashboard.unread_messages_count ?? 0;

  const organisationName =
    ossMe?.account?.organisation_name || t("fallback.ossName");

  /* ========================================================
     STATS
     ======================================================== */

  const stats = [
    {
      label: t("stats.newRequestsToday"),

      value: dashboard.new_requests_today_count ?? 0,
    },

    {
      label: t("stats.activeProjects"),

      value: dashboard.active_renovation_projects_count ?? 0,
    },

    {
      label: t("stats.unreadMessages", "Unread messages"),

      value: unreadMessagesCount,
    },
  ];

  /* ========================================================
     LOADING
     ======================================================== */

  if (loading) {
    return (
      <div className="oss-dashboard-view">
        <div className="oss-dashboard-loading">{t("messages.loading")}</div>
      </div>
    );
  }

  /* ========================================================
     UI
     ======================================================== */

  return (
    <div className="oss-dashboard-view">
      {/* ====================================================
          HERO
          ==================================================== */}

      <section className="oss-dashboard-hero">
        <p className="oss-dashboard-hero__eyebrow">
          {t("hero.welcome", {
            name: organisationName,
          }).toUpperCase()}
        </p>

        <h1 className="oss-dashboard-hero__title">{t("hero.subtitle")}</h1>

        <div className="oss-dashboard-stats">
          {stats.map((item) => (
            <div className="oss-dashboard-stat" key={item.label}>
              <Circle size={10} fill="currentColor" />

              <span>{item.label}</span>

              <strong>{item.value}</strong>
            </div>
          ))}
        </div>
      </section>

      {/* ====================================================
          PRIMARY ACTIONS
          ==================================================== */}

      <div className="oss-dashboard-action-grid">
        <article className="oss-dashboard-action-card">
          <span>{t("cards.requests.eyebrow")}</span>

          <h2>{t("cards.requests.title")}</h2>

          <p>{t("cards.requests.description")}</p>

          <button type="button" onClick={() => navigate("/oss/requests")}>
            {t("cards.requests.button")}
          </button>
        </article>

        <article className="oss-dashboard-action-card">
          <span>{t("cards.projects.eyebrow")}</span>

          <h2>{t("cards.projects.title")}</h2>

          <p>{t("cards.projects.description")}</p>

          <button type="button" onClick={() => navigate("/oss/projects")}>
            {t("cards.projects.button")}
          </button>
        </article>
      </div>

      {/* ====================================================
          DASHBOARD PANELS
          ==================================================== */}

      <div className="oss-dashboard-info-grid">
        {/* ==================================================
            LATEST PROJECT UPDATES
            ================================================== */}

        <section className="oss-dashboard-panel">
          <div className="oss-dashboard-panel__header">
            <div>
              <h2>{t("projectUpdates.title", "Latest project updates")}</h2>

              <p>
                {t(
                  "projectUpdates.subtitle",
                  "Recent changes across your renovation projects.",
                )}
              </p>
            </div>

            <span>
              {t("projectUpdates.badge", {
                count: latestProjectUpdatesTotal,

                defaultValue: `${latestProjectUpdatesTotal} updates`,
              })}
            </span>
          </div>

          <div className="oss-dashboard-list">
            {error && <div className="oss-dashboard-empty">{error}</div>}

            {!error && latestProjectUpdates.length === 0 && (
              <div className="oss-dashboard-empty">
                {t("projectUpdates.empty", "No recent project updates.")}
              </div>
            )}

            {latestProjectUpdates.map((item) => {
              const projectLabel = item.title || item.project_code || "—";

              const contextReference =
                item.loan_case_reference || item.dossier_code || "";

              return (
                <div
                  className="oss-dashboard-list-item"
                  key={item.project_id || item.project_code}
                >
                  <div className="oss-dashboard-list-main">
                    <strong>{projectLabel}</strong>

                    <p>
                      {item.homeowner_name || "—"}

                      {" · "}

                      {formatProjectStep(item.current_step_code, t)}
                    </p>

                    {contextReference && <small>{contextReference}</small>}

                    <small>
                      <Clock3 size={13} />

                      {formatDate(item.updated_at)}
                    </small>
                  </div>

                  <em>{formatProjectStatus(item.status, t)}</em>
                </div>
              );
            })}
          </div>
        </section>

        {/* ==================================================
            RECENT MESSAGES
            ================================================== */}

        <section className="oss-dashboard-panel">
          <div className="oss-dashboard-panel__header">
            <div>
              <h2>{t("recentMessages.title", "Recent messages")}</h2>

              <p>
                {t(
                  "recentMessages.subtitle",
                  "Latest messages received from homebuyers and lenders.",
                )}
              </p>
            </div>

            <span>
              {t("recentMessages.badge", {
                count: unreadMessagesCount,

                defaultValue: `${unreadMessagesCount} unread`,
              })}
            </span>
          </div>

          <div className="oss-dashboard-list">
            {latestMessages.length === 0 && (
              <div className="oss-dashboard-empty">
                {t("recentMessages.empty", "No recent messages.")}
              </div>
            )}

            {latestMessages.map((item) => {
              const context = getMessageContext(item);

              return (
                <button
                  type="button"
                  className={
                    item.is_read
                      ? "oss-dashboard-list-item oss-dashboard-message-item"
                      : "oss-dashboard-list-item oss-dashboard-message-item is-unread"
                  }
                  key={item.notification_id || item.message_id}
                  onClick={() => navigate("/oss/communication")}
                >
                  <div className="oss-dashboard-list-main">
                    <div className="oss-dashboard-message-sender">
                      <MessageCircle size={15} />

                      <strong>
                        {item.sender_name ||
                          item.thread_title ||
                          t("recentMessages.unknownSender", "Message")}
                      </strong>

                      {!item.is_read && (
                        <span className="oss-dashboard-message-unread-dot" />
                      )}
                    </div>

                    <p>
                      {truncateMessage(item.body) ||
                        t("recentMessages.noContent", "No message content.")}
                    </p>

                    <small>
                      {context && (
                        <>
                          <span>{context}</span>

                          <span>·</span>
                        </>
                      )}

                      <Clock3 size={13} />

                      {formatDate(item.created_at)}
                    </small>
                  </div>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="oss-dashboard-panel__view-all"
            onClick={() => navigate("/oss/communication")}
          >
            {t("recentMessages.viewAll", "View all messages")}
          </button>
        </section>
      </div>
    </div>
  );
}
